<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import { date, ValidationRule } from 'quasar';

import { formatDateIso, formatQDateOption, toDayUTCBishkek } from '@/shared/lib/date';
import UiInputDate from '@/shared/ui/input/date';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

export default defineComponent({
  name: 'EntityInputDateWithRule',
  components: { UiInputDate },
  props: {
    modelValue: {
      type: String as PropType<string | null>,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array as PropType<ValidationRule[]>,
      default: () => [],
    },
    noFuture: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    beforeDays: {
      type: String as PropType<string | null>,
      default: '',
    },
    afterDays: {
      type: String as PropType<string | null>,
      default: '',
    },
    lazyRules: {
      type: Boolean,
      default: true,
    },
    addDay: {
      type: Array<string>,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props) {
    const { t } = useI18n();
    const rul = ref<ValidationRule[]>([]);

    dayjs.extend(customParseFormat);
    const textError = t('SHARED.RULE.INCORRECT_DATE');
    const formatDate = { dot: 'DD.MM.YYYY', slash: 'YYYY/MM/DD' };
    const validateDate = (dateInput: string) => dayjs(dateInput, formatDate.dot, true).isValid();
    const optionDate = (e: string) => {
      const option = date.formatDate(e, formatDate.slash);
      if (props.beforeDays?.length && option > formatQDateOption(formatDateIso(props.beforeDays))) return false;
      if (props.afterDays?.length && option < formatQDateOption(formatDateIso(props.afterDays))) return false;
      if (props.addDay.length > 0 && option > toDayUTCBishkek()) return props.addDay.includes(option);
      return !(props.noFuture && option > toDayUTCBishkek());
    };
    const dayUnix = (date: string, format: string) => dayjs(date, format).unix();
    const ruleOptionDate = (e: string | null) => {
      if (!e && props.required) return textError;
      if (!e) return true;
      if (!validateDate(e)) return textError;
      if (props.beforeDays?.length && dayUnix(e, formatDate.dot) > dayUnix(props.beforeDays, formatDate.dot))
        return textError;
      if (props.afterDays?.length && dayUnix(e, formatDate.dot) < dayUnix(props.afterDays, formatDate.dot))
        return textError;

      const isFuture = dayUnix(e, formatDate.dot) > dayUnix(toDayUTCBishkek(), formatDate.slash);
      if (props.addDay.length && isFuture)
        return props.addDay.includes(formatQDateOption(formatDateIso(e))) ? true : textError;
      if (props.noFuture && isFuture) return textError;
      return true;
    };

    return {
      rul,
      optionDate,
      ruleOptionDate,
    };
  },
});
</script>

<template>
  <ui-input-date
    :model-value="modelValue"
    :options="optionDate"
    :rules="[...rules, ruleOptionDate]"
    :label="label"
    :loading="loading"
    :disable="disable"
    :lazy-rules="lazyRules"
    :hint="hint"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>
